import React, { useMemo } from 'react';

import { TextFieldProps } from '@mui/material/TextField';
import { SxProps, Theme } from '@mui/material/styles';
import { DatePicker, DatePickerProps } from '@mui/x-date-pickers/DatePicker';

import { withTitle } from '../../hoc/with-title';
import {
  useWindowedTooltipZIndex,
  withWindowedPopperProps,
} from '../dialog/windowed-tooltip-zindex-context';

type DatePickerFieldProps = Omit<DatePickerProps<any>, 'renderInput'> & {
  value?: TextFieldProps['value'];
  size?: TextFieldProps['size'];
  fullWidth?: TextFieldProps['fullWidth'];
  onChange?: (value: string | null) => void;
  sx?: TextFieldProps['sx'];
};

export const DatePickerField = ({
  value,
  label,
  size,
  fullWidth,
  onChange,
  disabled,
  sx,
  minDate,
  maxDate,
}: DatePickerFieldProps) => {
  const windowedConfig = useWindowedTooltipZIndex();
  const slotProps = {
    popper: withWindowedPopperProps(windowedConfig, undefined),
    textField: {
      fullWidth,
      size,
      error: false,
      disabled,
      sx: {
        ...sx,
        '& .MuiInputAdornment-root': {
          width: 'auto',
          height: 'auto',
          '& .MuiSvgIcon-root': {
            fontSize: '1.5rem',
          },
        },
        '& .MuiInputBase-input': {
          color: sx && getValueFromSx(sx, 'color'),
          fontSize: sx && getValueFromSx(sx, 'fontSize'),
        },
      },
    },
  };

  const updatedValue = useMemo(() => {
    if (!value) return '';
    return new Date(value) as any;
  }, [value]);

  return (
    <DatePicker
      value={updatedValue || null}
      label={label}
      sx={{ ...sx, color: 'red' }}
      slotProps={slotProps}
      disabled={disabled}
      minDate={minDate}
      maxDate={maxDate}
      onChange={(newVal) => {
        if (onChange) {
          onChange(newVal ? newVal.toString() : null);
        }
      }}
    />
  );
};

export const DatePickerWithTitle = withTitle(DatePickerField);

function getValueFromSx(
  sx: SxProps<Theme>,
  propName: string
): string | undefined {
  if (!sx) return undefined;

  if (Array.isArray(sx)) {
    for (const item of sx) {
      if (typeof item === 'object' && item && propName in item) {
        return (item as any)[propName];
      }
    }
  }

  if (typeof sx === 'function') {
    return undefined; // You could optionally evaluate with theme
  }

  if (typeof sx === 'object' && propName in sx) {
    return (sx as any)[propName];
  }

  return undefined;
}
